window.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.c-booking-form').forEach(initBookingForm);
  initContactForm();
});

/**
 * Initializes the booking form.
 * @param {HTMLFormElement} form - The form element to initialize.
 */
function initBookingForm(form) {
  const arrival = form.querySelector('.field-arrival input');
  const departure = form.querySelector('.field-departure input');
  const bookingRooms = form.querySelector('.booking-rooms');
  if (!arrival || !departure || !bookingRooms) return;
  initBookingRooms(arrival, departure, bookingRooms);
  toggleActiveStates(arrival, departure, bookingRooms);
}

export function initContactForm() {
  window.addEventListener('UC_UI_INITIALIZED', () => {
    const captchaCookie = checkCaptchaCookieStatus();
    setByCookieStatus(captchaCookie);
    if (captchaCookie) initShadowRootCookieNoticeButtons();
  });
}

function checkCaptchaCookieStatus() {
  if (!window.UC_UI) return false;

  return (
    window.UC_UI.getServicesBaseInfo().find(
      (service) => service.name === 'reCaptcha v3'
    ).consent.status === true
  );
}

function initShadowRootCookieNoticeButtons() {
  window.addEventListener('UC_UI_CMP_EVENT', function (e) {
    const types = ['ACCEPT_ALL', 'DENY_ALL', 'SAVE'];
    if (e.detail && types.includes(e.detail.type)) {
      setByCookieStatus(checkCaptchaCookieStatus());
    }
  });
}

function setByCookieStatus(active = false) {
  const form = document.querySelector('.c-contact-form');
  const warning = form?.querySelector('.form-warning');
  const sendButton = form?.querySelector('button[type="submit"]');
  const acceptButton = form?.querySelector('.accept-captcha-cookie');

  warning?.classList?.toggle('hidden', active);
  if (sendButton) sendButton.disabled = !active;

  if (!active) {
    acceptButton?.addEventListener('click', acceptCaptchaCookie);
  } else {
    acceptButton?.removeEventListener('click', acceptCaptchaCookie);
  }
}

function acceptCaptchaCookie() {
  window.UC_UI?.acceptService('cfADcn3E3')?.then(() => setByCookieStatus(true));
}

/**
 * Toggles the disabled state of the form elements based on the arrival and departure dates.
 * @param {HTMLInputElement} arrival - The arrival date input element.
 * @param {HTMLInputElement} departure - The departure date input element.
 * @param {HTMLElement} bookingRooms - The booking rooms container element.
 */
function toggleActiveStates(arrival, departure, bookingRooms) {
  const arrivalDate = arrival.valueAsDate;
  const departureDate = departure.valueAsDate;

  const elements = bookingRooms.querySelectorAll('.c-text-field, .c-select, .c-checkbox, .c-button');
  const submitButton = bookingRooms.closest('form').querySelector('button[type="submit"]');

  [...elements, submitButton].forEach(element => {
    element.disabled = !arrivalDate || !departureDate || arrivalDate >= departureDate;

    //if (element.parentNode.classList.contains('field-people')) {
    //  element.disabled = element.value < 2;
    //}
  });
}

/**
 * Initializes the booking rooms.
 * @param {HTMLInputElement} arrival - The arrival date input element.
 * @param {HTMLInputElement} departure - The departure date input element.
 * @param {HTMLElement} bookingRooms - The booking rooms container element.
 */
function initBookingRooms(arrival, departure, bookingRooms) {
  initArrivalAndDeparture(arrival, departure);

  [arrival, departure].forEach(dateField => {
    dateField.addEventListener('change', () => {
      const arrivalDate = arrival.valueAsDate;
      const departureDate = departure.valueAsDate;
      if (!arrivalDate || !departureDate) return;

      bookingRooms.querySelectorAll('.booking-room.show').forEach(room => {
        const value = room.querySelector('.field-room-type select').value;
        const data = room.querySelector(`option[value="${value}"]`)?.dataset;
        updateRoomPrice(bookingRooms, room, data, arrivalDate, departureDate);
      });

      toggleActiveStates(arrival, departure, bookingRooms);
    });
  });

  bookingRooms.querySelectorAll('.booking-room.show').forEach(bookingRoom => {
    const target = bookingRoom.querySelector('.field-room-type select');
    if (!target) return;
    const data = bookingRoom.querySelector(`option[value="${target.value}"]`)?.dataset;
    updateRoom(data, bookingRoom.querySelector('.field-people select'));
  });

  bookingRooms.addEventListener('change', (e) => {
    /** @type {HTMLInputElement} */
    const target = e.target;
    const room = target?.closest('.booking-room');
    const arrivalDate = arrival.valueAsDate;
    const departureDate = departure.valueAsDate;

    if (!room || !arrivalDate || !departureDate) return;

    const parentClass = target.closest('.c-field-container').classList;
    const value = room.querySelector('.field-room-type select').value;
    let data = room.querySelector(`option[value="${value}"]`)?.dataset;

    if (parentClass.contains('field-room-type')) {
      data = room.querySelector(`option[value="${target.value}"]`)?.dataset;
      updateRoom(data, room.querySelector('.field-people select'));
    }

    if (
      parentClass.contains('field-room-type')
      || parentClass.contains('field-people')
      || parentClass.contains('field-extra-breakfast')
    ) updateRoomPrice(bookingRooms, room, data, arrivalDate, departureDate);
  });

  initAddRoomButton(bookingRooms, arrival, departure);
  fixRoomNumbers(bookingRooms);
}

/**
 * Initializes the arrival and departure date fields.
 * @param {HTMLInputElement} arrival - The arrival date input element.
 * @param {HTMLInputElement} departure - The departure date input element.
 */
function initArrivalAndDeparture(arrival, departure) {
  // Add a listener to the arrival date field
  arrival.addEventListener('change', () => {
    let nextDayDate = new Date(arrival.valueAsDate);
    nextDayDate.setDate(nextDayDate.getDate() + 1);
    departure.min = new Date(nextDayDate.getTime() - (nextDayDate.getTimezoneOffset() * 60000)).toISOString().split("T")[0];

    // Update the departure date field if necessary
    if (departure.valueAsDate < arrival.valueAsDate) {
      departure.valueAsDate = nextDayDate;
    }
  });

  // Add a listener to the departure date field
  departure.addEventListener('change', () => {
    // Update the arrival date field if necessary
    if (arrival > departure) {
      arrival.value = departure.value;
    }
  });
}

/**
 * Gets the prices for a room type.
 * @param {Object} data - The data object for the room type.
 * @returns {Array} An array of objects containing the number of people and the price.
 */
function getPrices(data) {
  return (data.prices.split(';')).map(price => {
    const pricing = price.split(':');
    return {'people': pricing[0], 'price': pricing[1]}
  });
}

/**
 * Updates the room type options in the people select element.
 * @param {Object} data - The data object for the room type.
 * @param {HTMLSelectElement} peopleElement - The people select element.
 */
function updateRoom(data, peopleElement) {
  peopleElement.disabled = data.people < 2;
  peopleElement.querySelectorAll(`option`).forEach(roomOption => {
    roomOption.classList.toggle('hidden', roomOption.dataset.room !== data.room)
    roomOption.selected = roomOption.dataset.room === data.room && roomOption.value === data.people;
  });
}

/**
 * Updates the price of a room based on the arrival and departure dates.
 * Also updates the calculated price.
 * @param {HTMLElement} bookingRooms - The booking rooms container element.
 * @param {HTMLElement} room - The room element.
 * @param {Object} data - The data object for the room type.
 * @param {Date} arrivalDate - The arrival date.
 * @param {Date} departureDate - The departure date.
 */
function updateRoomPrice(bookingRooms, room, data, arrivalDate, departureDate) {
  const withBreakfast = room.querySelector('.field-extra-breakfast input').checked;
  const peopleValue = parseInt(room.querySelector('.field-people select').value);
  if (peopleValue < 1) return;

  const nights = Math.ceil((departureDate - arrivalDate) / (1000 * 60 * 60 * 24));
  const prices = getPrices(data).filter(pricing => parseInt(pricing.people) === peopleValue);

  if (!prices.length) return;
  let price = parseInt(prices[0].price) * nights;

  if (withBreakfast) price += 9 * nights * peopleValue;

  room.querySelector('.c-price').innerText = `${price} €`;
  updateCalculatedPrice(bookingRooms, price);
}

/**
 * Updates the calculated price based on the prices of the rooms.
 * @param {HTMLElement} bookingRooms - The booking rooms container element.
 * @param {Number} price - The total price of the rooms.
 */
function updateCalculatedPrice(bookingRooms, price) {
  let calculatedPrice = 0;

  bookingRooms.querySelectorAll('.c-price').forEach(priceElement => {
    calculatedPrice += parseInt(priceElement.innerText);
  });

  bookingRooms.parentNode.querySelector('.price-info .c-price').innerText = `${calculatedPrice} €`;
}

/**
 * Initializes the add room button.
 * @param {HTMLElement} bookingRooms - The booking rooms container element.
 * @param {HTMLInputElement} arrival - The arrival date input element.
 * @param {HTMLInputElement} departure - The departure date input element.
 */
function initAddRoomButton(bookingRooms, arrival, departure) {
  const addRoomButton = bookingRooms.querySelector('.add-booking-room button');

  // Add a room
  addRoomButton.addEventListener('click', (e) => {
    //e.preventDefault();
    bookingRooms.querySelector('.booking-room:not(.show)').classList.add('show');
    fixRoomNumbers(bookingRooms);
    toggleAddRoomButton();
  });

  // Remove a participant
  bookingRooms.addEventListener('click', (e) => {
    const target = e.target;
    if (target.classList.contains('remove-booking-room')) {
      const bookingRoom = target.closest('.booking-room');
      bookingRoom.closest('.booking-room').classList.remove('show');
      bookingRoom.querySelector('.field-room-type select').value = 'none';
      bookingRoom.querySelector('.field-people select').value = '';
      bookingRoom.querySelector('.calculated-room-price .c-price').innerText = '0,00 €';
      toggleAddRoomButton();
      fixRoomNumbers(bookingRooms);
      updateCalculatedPrice(bookingRooms, 0);
    }
  });

  function toggleAddRoomButton() {
    const allRooms = bookingRooms.querySelectorAll('.booking-room').length;
    const visibleRooms = bookingRooms.querySelectorAll('.booking-room.show').length;
    addRoomButton.classList.toggle('hidden', allRooms === visibleRooms);
  }

  toggleAddRoomButton();
}

/**
 * Fixes the room numbers in the booking rooms.
 * @param {HTMLElement} bookingRooms - The booking rooms container element.
 */
function fixRoomNumbers(bookingRooms) {
  bookingRooms.querySelectorAll('.room-number').forEach((room, index) => {
    room.innerText = index + 1;
  });
}
